// Explore-stats page: Legislation passage statistics (issue #40).
// How many days bills take to pass through each House, derived from each
// bill's structured progress_timeline (chamber + ISO date per stage).

import React, { useState, useEffect } from 'react';
import { Paper, Grid, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatLine } from './charts';

function StatCards({ cards }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((c) => (
        <Grid item xs={6} md={4} key={c.label}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h3" color="primary">{c.value}</Typography>
            <Typography variant="body2" color="text.secondary">{c.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// Fastest / Longest to assent cards (#265). Both renderers share one component;
// `record` is `{bill_id, title, intro_date, assent_date, days}` or null.
function AssentCard({ label, record }) {
  if (!record) return null;
  const days = (record.days || 0).toLocaleString();
  return (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h3" color="primary" sx={{ mt: 0.5, mb: 1 }}>
        {days} {record.days === 1 ? 'day' : 'days'}
      </Typography>
      {record.bill_id ? (
        <Link component={RouterLink} to={`/bills/${record.bill_id}`}
              underline="hover" sx={{ fontWeight: 500 }}>
          {record.title || record.bill_id}
        </Link>
      ) : (
        <Typography sx={{ fontWeight: 500 }}>
          {record.title || '(untitled)'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="div"
                  sx={{ mt: 0.5 }}>
        introduced {record.intro_date} → assented {record.assent_date}
      </Typography>
    </Paper>
  );
}

export default function LegislationPassage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('legislation-passage');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Legislation passage statistics"
      description={`How long bills take to pass through each House${
        c.total ? ` (${c.total} bills measured; ${c.shown} most recent in the table)` : ''
      }. Durations are derived from each bill's progress timeline: "Days in House" / "Days in Senate" is the span between that bill's first and last dated stage in that chamber, and "Total days" is the span from its earliest to its latest dated stage. Bills without enough dated timeline stages are excluded rather than estimated.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="legislation-passage"
    >
      {data && (
        <>
          <StatCards cards={c.stat_cards || []} />
          {(c.fastest_passage || c.longest_passage) && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {c.fastest_passage && (
                <Grid item xs={12} md={6}>
                  <AssentCard label="Fastest to assent" record={c.fastest_passage} />
                </Grid>
              )}
              {c.longest_passage && (
                <Grid item xs={12} md={6}>
                  <AssentCard label="Longest to assent" record={c.longest_passage} />
                </Grid>
              )}
            </Grid>
          )}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Average passage time by Parliament
            </Typography>
            <StatLine data={c.avg_by_parliament || []} xKey="parliament"
                      keys={c.series || ['Avg days in House', 'Avg days in Senate', 'Avg total days']} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
